import { GenericResponse } from '@/types/downloadReports';

import { createAction, createAsyncAction } from 'typesafe-actions';

import { WidgetKeys } from '../enums';

import {
  CashflowThroughTimeState,
  DebtHedgeScheduleState,
  FailurePayload,
  FetchDebtsSuccessPayload,
  FetchIRIndicesSuccessPayload,
  FetchSwapTenorSuccessPayload,
  FetchTradeDebtRequestPayload,
  FetchTradesSuccessPayload,
  IRMarketEnvironmentState,
  PricingSummaryState,
  UserWidgetResponse,
  ValidateDebtsPayload,
  ValidateIrIndexFailurePayload,
  ValidateIrIndexPayload,
  ValidateIrIndexSuccessPayload,
  ValidateTradesPayload,
  ValidateWidgetFieldFailurePayload,
  ValidateWidgetFieldSuccessPayload,
} from './types';

// ─────────────────────────────────────────────
// IR ANALYSIS ACTIONS (uniform naming, no constants)
// ─────────────────────────────────────────────

export const fetchIRIndicesActions = createAsyncAction(
  'IR_ANALYSIS/fetchIRIndices/request',
  'IR_ANALYSIS/fetchIRIndices/success',
  'IR_ANALYSIS/fetchIRIndices/failure',
)<void, FetchIRIndicesSuccessPayload, FailurePayload>();

export const fetchSwapTenorsActions = createAsyncAction(
  'IR_ANALYSIS/fetchSwapTenors/request',
  'IR_ANALYSIS/fetchSwapTenors/success',
  'IR_ANALYSIS/fetchSwapTenors/failure',
)<void, FetchSwapTenorSuccessPayload, FailurePayload>();

export const validateIrIndexActions = createAsyncAction(
  'IR_ANALYSIS/validateIrIndex/request',
  'IR_ANALYSIS/validateIrIndex/success',
  'IR_ANALYSIS/validateIrIndex/failure',
)<
  ValidateIrIndexPayload,
  ValidateIrIndexSuccessPayload,
  ValidateIrIndexFailurePayload
>();

export const downloadRMToolsReportActions = createAsyncAction(
  'IR_ANALYSIS/downloadRMToolsReport/request',
  'IR_ANALYSIS/downloadRMToolsReport/success',
  'IR_ANALYSIS/downloadRMToolsReport/failure',
)<void, GenericResponse, FailurePayload>();

export const fetchTradesActions = createAsyncAction(
  'IR_ANALYSIS/fetchTrades/request',
  'IR_ANALYSIS/fetchTrades/success',
  'IR_ANALYSIS/fetchTrades/failure',
)<
  FetchTradeDebtRequestPayload,
  FetchTradesSuccessPayload,
  FailurePayload & { widgetType: WidgetKeys }
>();

export const fetchDebtsActions = createAsyncAction(
  'IR_ANALYSIS/fetchDebts/request',
  'IR_ANALYSIS/fetchDebts/success',
  'IR_ANALYSIS/fetchDebts/failure',
)<
  FetchTradeDebtRequestPayload,
  FetchDebtsSuccessPayload,
  FailurePayload & { widgetType: WidgetKeys }
>();

export const fetchUserWidgetsActions = createAsyncAction(
  'IR_ANALYSIS/fetchUserWidgets/request',
  'IR_ANALYSIS/fetchUserWidgets/success',
  'IR_ANALYSIS/fetchUserWidgets/failure',
)<void, { widgets: UserWidgetResponse[] }, FailurePayload>();

export const getClosestTradeValuationDateActions = createAsyncAction(
  'IR_ANALYSIS/getClosestTradeValuationDate/request',
  'IR_ANALYSIS/getClosestTradeValuationDate/success',
  'IR_ANALYSIS/getClosestTradeValuationDate/failure',
)<void, { value_date: string }, FailurePayload>();

export const validateTradesActions = createAsyncAction(
  'IR_ANALYSIS/validateTrades/request',
  'IR_ANALYSIS/validateTrades/success',
  'IR_ANALYSIS/validateTrades/failure',
)<
  ValidateTradesPayload,
  ValidateWidgetFieldSuccessPayload,
  ValidateWidgetFieldFailurePayload
>();

export const validateDebtsActions = createAsyncAction(
  'IR_ANALYSIS/validateDebts/request',
  'IR_ANALYSIS/validateDebts/success',
  'IR_ANALYSIS/validateDebts/failure',
)<
  ValidateDebtsPayload,
  ValidateWidgetFieldSuccessPayload,
  ValidateWidgetFieldFailurePayload
>();

export const saveUserWidgetDataActions = createAsyncAction(
  'IR_ANALYSIS/saveUserWidgetData/request',
  'IR_ANALYSIS/saveUserWidgetData/success',
  'IR_ANALYSIS/saveUserWidgetData/failure',
)<undefined, { success: boolean }, string>();

export const updatePricingSummaryActions = createAsyncAction(
  'IR_ANALYSIS/updatePricingSummary/request',
  'IR_ANALYSIS/updatePricingSummary/success',
  'IR_ANALYSIS/updatePricingSummary/failure',
)<undefined, Partial<PricingSummaryState>, string>();

export const updateDebtHedgeScheduleActions = createAsyncAction(
  'IR_ANALYSIS/updateDebtHedgeSchedule/request',
  'IR_ANALYSIS/updateDebtHedgeSchedule/success',
  'IR_ANALYSIS/updateDebtHedgeSchedule/failure',
)<undefined, Partial<DebtHedgeScheduleState>, string>();

export const updateCashflowThroughTimeActions = createAsyncAction(
  'IR_ANALYSIS/updateCashflowThroughTime/request',
  'IR_ANALYSIS/updateCashflowThroughTime/success',
  'IR_ANALYSIS/updateCashflowThroughTime/failure',
)<undefined, Partial<CashflowThroughTimeState>, string>();

export const updateIRMarketEnvironmentActions = createAsyncAction(
  'IR_ANALYSIS/updateIRMarketEnvironment/request',
  'IR_ANALYSIS/updateIRMarketEnvironment/success',
  'IR_ANALYSIS/updateIRMarketEnvironment/failure',
)<undefined, Partial<IRMarketEnvironmentState>, string>();

export const disableDebtAndHedgeAction = createAction(
  'IR_ANALYSIS/disableDebtAndHedge',
)<boolean>();

export const disableCFTTAction = createAction(
  'IR_ANALYSIS/disableCFTT',
)<boolean>();